// Error logging for the web app.
// Development: errors go to the browser console as before.
// Production: errors are reported to the backend (POST /api/client-errors) and end up in Loki as
// {job="riselio", logger="frontend"}; nothing is printed to the console.

import { API_CONFIG, APP_VERSION, isProduction } from '../config/api.config';

export type ErrorKind = 'logged' | 'boundary' | 'window' | 'promise';

interface ReportOptions {
  kind?: ErrorKind;
  componentStack?: string;
}

const ENDPOINT = `${API_CONFIG.BASE_URL}/client-errors`;
// A broken page can throw in a loop; the backend rate-limits too, but don't even try past this
const MAX_REPORTS_PER_PAGE = 20;

const reported = new Set<string>();
let reportCount = 0;

interface Described {
  message: string;
  stack?: string;
  status?: number;
  requestId?: string;
}

const describe = (error: unknown): Described => {
  if (error instanceof Error) {
    const httpError = error as Error & { response?: { status?: number }; requestId?: string };
    return {
      message: `${error.name}: ${error.message}`,
      stack: error.stack,
      status: httpError.response?.status,
      requestId: httpError.requestId,
    };
  }
  if (typeof error === 'string') {
    return { message: error };
  }
  if (error === undefined || error === null) {
    return { message: '' };
  }
  try {
    return { message: JSON.stringify(error).slice(0, 500) };
  } catch {
    return { message: String(error) };
  }
};

const send = (body: Record<string, unknown>): void => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const token = localStorage.getItem(API_CONFIG.TOKEN_KEYS.ACCESS_TOKEN);
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    // Storage blocked (private mode): report anonymously
  }
  // keepalive lets the report finish when the page is being unloaded
  fetch(ENDPOINT, { method: 'POST', headers, body: JSON.stringify(body), keepalive: true }).catch(() => {
    // Reporting must never cause another error
  });
};

const report = (context: string, error: unknown, options: ReportOptions = {}): void => {
  const described = describe(error);
  // Client errors (4xx) are expected outcomes and already in the backend logs
  if (described.status !== undefined && described.status < 500) {
    return;
  }
  const message = [context, described.message].filter(Boolean).join(' | ');
  const key = `${options.kind ?? 'logged'}|${message}|${described.stack?.split('\n')[1] ?? ''}`;
  if (reported.has(key) || reportCount >= MAX_REPORTS_PER_PAGE) {
    return;
  }
  reported.add(key);
  reportCount += 1;

  send({
    kind: options.kind ?? 'logged',
    message,
    stack: described.stack,
    component_stack: options.componentStack,
    url: window.location.href,
    release: APP_VERSION,
    request_id: described.requestId,
  });
};

export const logger = {
  /** Log an error: `context` says what failed (a short English phrase or an i18n key), `error` is what was caught. */
  error(context: string, error?: unknown, options?: ReportOptions): void {
    if (!isProduction) {
      console.error(context, ...(error === undefined ? [] : [error]), ...(options?.componentStack ? [options.componentStack] : []));
      return;
    }
    report(context, error, options);
  },
};

let globalHandlersInstalled = false;

/** Report errors nothing else caught: uncaught exceptions and unhandled promise rejections. */
export const installGlobalErrorHandlers = (): void => {
  // In development the browser and the dev server overlay already show these
  if (!isProduction || globalHandlersInstalled) {
    return;
  }
  globalHandlersInstalled = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    // Resource load failures (img, script) arrive without an error object; they are not code errors
    if (!event.error && !event.message) {
      return;
    }
    logger.error('Uncaught error', event.error ?? event.message, { kind: 'window' });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    logger.error('Unhandled promise rejection', event.reason, { kind: 'promise' });
  });
};

/** Test helper: forget what was already reported. */
export const resetLoggerForTests = (): void => {
  reported.clear();
  reportCount = 0;
};
